interface CacheItem<T> {
  data: T;
  timestamp: number;
  expires: number;
}

class ApiCache {
  private cache = new Map<string, CacheItem<any>>();
  private defaultTTL = 5 * 60 * 1000; // 5 minutes

  set<T>(key: string, data: T, ttl?: number): void {
    try {
      // Validate key
      if (!key || typeof key !== 'string') {
        console.warn('Invalid cache key provided:', key);
        return;
      }
      
      const expires = ttl || this.defaultTTL;
      const cacheItem: CacheItem<T> = {
        data,
        timestamp: Date.now(),
        expires: Date.now() + expires
      };
      this.cache.set(key, cacheItem);
      console.log(`Cache set for key: ${key}, expires in: ${expires}ms`);
    } catch (error) {
      console.error('Error setting cache item:', error);
    }
  }

  get<T>(key: string): T | null {
    try {
      // Validate key
      if (!key || typeof key !== 'string') {
        console.warn('Invalid cache key provided:', key);
        return null;
      }
      
      const item = this.cache.get(key);
      if (!item) {
        console.log(`Cache miss for key: ${key}`);
        return null;
      }
      
      if (Date.now() > item.expires) {
        console.log(`Cache expired for key: ${key}`);
        this.cache.delete(key);
        return null;
      }
      
      console.log(`Cache hit for key: ${key}`);
      return item.data;
    } catch (error) {
      console.error('Error getting cache item:', error);
      return null;
    }
  }

  has(key: string): boolean {
    try {
      const item = this.cache.get(key);
      if (!item) return false;
      
      if (Date.now() > item.expires) {
        this.cache.delete(key);
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Error checking cache item:', error);
      return false;
    }
  }

  delete(key: string): void {
    try {
      this.cache.delete(key);
      console.log(`Cache deleted for key: ${key}`);
    } catch (error) {
      console.error('Error deleting cache item:', error);
    }
  }

  clear(): void {
    try {
      const size = this.cache.size;
      this.cache.clear();
      console.log(`Cache cleared, ${size} items removed`);
    } catch (error) {
      console.error('Error clearing cache:', error);
    }
  }

  // Clear expired items
  cleanup(): void {
    try {
      const now = Date.now();
      let count = 0;
      for (const [key, item] of this.cache.entries()) {
        if (now > item.expires) {
          this.cache.delete(key);
          count++;
        }
      }
      if (count > 0) {
        console.log(`Cache cleanup: ${count} expired items removed`);
      }
    } catch (error) {
      console.error('Error during cache cleanup:', error);
    }
  }

  // Generate cache key from object
  generateKey(prefix: string, params?: Record<string, any>): string {
    try {
      if (!prefix || typeof prefix !== 'string') {
        throw new Error('Prefix is required and must be a string');
      }
      
      if (!params) return prefix;
      
      // Create a sorted, stable representation of the params
      const sortedParams = Object.keys(params)
        .sort()
        .reduce((result, key) => {
          // Skip undefined values
          if (params[key] !== undefined) {
            result[key] = params[key];
          }
          return result;
        }, {} as Record<string, any>);
      
      // Handle empty params
      if (Object.keys(sortedParams).length === 0) {
        return prefix;
      }
      
      const key = `${prefix}:${JSON.stringify(sortedParams)}`;
      console.log(`Generated cache key: ${key}`);
      return key;
    } catch (error) {
      console.error('Error generating cache key:', error);
      return prefix;
    }
  }
}

export const apiCache = new ApiCache();

// Cleanup expired items every 10 minutes
setInterval(() => {
  apiCache.cleanup();
}, 10 * 60 * 1000);